import React, { useMemo, useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../TransactionsTableComponent/TransactionsTableComponent.module.css";
import rowstyles from "./TransactionsTableRow.module.css";
import { splitTagPathsCell } from "../../UploadDataComponent/utils/tagUtils";
import { parseAmountValue, formatAmountDisplay } from "../TransactionsTableComponent/TransactionsTableComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faPen, faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons";

/* ---------------- Local helpers ---------------- */

function safeJSONStringify(value) {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (key, val) => {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[Circular]";
          seen.add(val);
        }
        return val;
      }
    );
  } catch {
    try { return String(value); } catch { return ""; }
  }
}

function accountNameFromId(id, accounts) {
  if (!id) return undefined;
  const found = accounts.find(a => String(a.id) === String(id));
  return found ? found.name : undefined;
}
function accountIdFromName(name, accounts) {
  if (!name) return undefined;
  const found = accounts.find(a => String(a.name) === String(name));
  return found ? found.id : undefined;
}

function isAccountCol(key) { return /account/i.test(key) && !/id$/i.test(key); }
function isDateISO(s) { return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "")); }
function coerceDateISO(s) {
  const t = String(s || "").trim();
  if (isDateISO(t)) return t;
  const ms = Date.parse(t);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
}

function isDescriptionCol(key) { return /description|memo|note/i.test(key); }
function isTagsCol(key) { return /tag/i.test(key); }
function isAmountCol(key) { return /amount$/i.test(key); }
function isBalanceCol(key) { return /balance$/i.test(key); }

function isSearchable(key) {
  return isDescriptionCol(key) || /date/i.test(key) || isAccountCol(key);
}

function isEditable(key) {
  if (isBalanceCol(key)) return false;
  if (/^id$/i.test(key) || /createdAt|updatedAt/i.test(key)) return false;
  if (isAmountCol(key)) return true;
  if (isTagsCol(key)) return true;
  if (isDescriptionCol(key)) return true;
  if (/date/i.test(key)) return true;
  if (isAccountCol(key)) return true;
  return false;
}

/* Normalize tags for display (dedupe, preserve order) */
function normalizeTagsForDisplay(row) {
  const out = [];
  const seen = new Set();

  const pushOne = (s) => {
    const v = String(s || "").trim();
    if (!v) return;
    if (!seen.has(v)) { seen.add(v); out.push(v); }
  };

  if (Array.isArray(row?.tagPath)) {
    row.tagPath.forEach(pushOne);
    return out;
  }
  if (Array.isArray(row?.tagPaths)) {
    row.tagPaths.forEach((p) => {
      if (Array.isArray(p)) pushOne(p.join("/"));
      else pushOne(String(p));
    });
    return out;
  }
  // Fall back to any legacy string-ish cell via helper
  splitTagPathsCell(String(row?.tagPath ?? row?.tagPaths ?? "")).forEach(pushOne);
  return out;
}

/* Build a readable "From → To" label for transfers (merged row) */
function buildTransferRouteText(row, accounts) {
  const leftName =
    row.fromAccountName ||
    accountNameFromId(row.fromAccountId, accounts) ||
    (row.fromAccountId ? String(row.fromAccountId) : undefined);

  const rightName =
    row.toAccountName ||
    accountNameFromId(row.toAccountId, accounts) ||
    (row.toAccountId ? String(row.toAccountId) : undefined);

  const left = leftName || "?";
  const right = rightName || "?";
  return { left, right, both: `${left} → ${right}` };
}

/* Fallback route for single-leg rows */
function buildSingleLegRouteText(row) {
  const accName =
    row.accountName || row.account_name || String(row.accountId || row.account_id || "") || "?";
  const side = String(row.transferSide || "").toUpperCase();
  if (side === "OUT") return { left: accName || "?", right: "?", both: `${accName || "?"} → ?` };
  if (side === "IN") return { left: "?", right: accName || "?", both: `? → ${accName || "?"}` };
  return { left: accName || "?", right: "", both: accName || "?" };
}

/* ---------------- Component ---------------- */

export default function TransactionsTableRow({
  row,
  id,
  visibleCols,
  selectable = false,
  isChecked = false,
  absoluteIndex = 0,
  onToggleRow,
  wrapDescription = false,
  tagsAsChips = true,

  // Inline edit API (from table)
  inlineEditEnabled = false,
  onCommitPatch,         // (row, patch) => Promise<void>
  accounts = [],         // [{id, name, ...}]
  accountOptions = [],   // ["Chequing", "Savings", ...]
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const isMergedTransfer = !!(row && row.__merged && row.typeName === "transfer" && row.__legs);
  const isTransfer = String(row?.typeName || "").toLowerCase() === "transfer";

  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // drafts for merged-transfer account editing (two selectors)
  const [fromIdDraft, setFromIdDraft] = useState("");
  const [toIdDraft, setToIdDraft] = useState("");

  useEffect(() => {
    if (editingKey && inputRef.current) {
      inputRef.current.focus();
      if (typeof inputRef.current.select === "function") inputRef.current.select();
    }
  }, [editingKey]);

  const applyFilter = (kind, value) => {
    const sp = new URLSearchParams(location.search);
    if (kind === "date") {
      const iso = coerceDateISO(value);
      if (!iso) return;
      sp.set("dateFrom", iso);
      sp.set("dateTo", iso);
      sp.delete("range");
    } else if (kind === "account") {
      sp.set("account", String(value || ""));
    } else if (kind === "q") {
      sp.set("q", String(value || ""));
    } else if (kind === "tag") {
      sp.set("tag", String(value || ""));
    }
    navigate({ pathname: location.pathname, search: sp.toString() });
  };

  const beginEdit = (key) => {
    if (!inlineEditEnabled || !isEditable(key) || saving) return;

    // two-account edit for merged transfers
    if (isMergedTransfer && isAccountCol(key)) {
      setEditingKey(key);
      const fromId =
        row.fromAccountId ??
        accountIdFromName(row.fromAccountName, accounts) ??
        "";
      const toId =
        row.toAccountId ??
        accountIdFromName(row.toAccountName, accounts) ??
        "";
      setFromIdDraft(String(fromId || ""));
      setToIdDraft(String(toId || ""));
      return;
    }

    setEditingKey(key);
    const raw = row?.[key];

    if (isAmountCol(key)) {
      if (isMergedTransfer) {
        const n = parseAmountValue(row?.amountAbs ?? row?.amount);
        setDraft(Number.isFinite(n) ? String(Math.abs(n)) : "");
        return;
      }
      const n = parseAmountValue(raw);
      setDraft(Number.isFinite(n) ? String(n) : String(raw ?? ""));
      return;
    }

    if (isTagsCol(key)) {
      // Use normalized display tags for editing value
      const arr = normalizeTagsForDisplay(row);
      setDraft(arr.join(", "));
      return;
    }

    if (/date/i.test(key)) {
      setDraft(coerceDateISO(raw) || "");
      return;
    }

    setDraft(String(raw ?? ""));
  };

  const cancelEdit = () => {
    if (saving) return;
    setEditingKey(null);
    setDraft("");
  };

  const commitEdit = async () => {
    if (!editingKey || saving) return;

    const key = editingKey;

    try {
      let patch = {};

      if (isMergedTransfer && isAccountCol(key)) {
        // Allow same src/dest — balance-adjustment style transfers are valid
        const fromId = fromIdDraft || "";
        const toId = toIdDraft || "";
        const fromName = accountNameFromId(fromId, accounts);
        const toName = accountNameFromId(toId, accounts);
        patch = {
          fromAccountId: fromId || undefined,
          toAccountId: toId || undefined,
          fromAccountName: fromName || undefined,
          toAccountName: toName || undefined,
        };
      } else if (isAmountCol(key)) {
        let num = parseAmountValue(draft);
        if (!Number.isFinite(num)) throw new Error("Amount must be a number.");

        if (isMergedTransfer) {
          patch = { amountAbs: Math.abs(num) };
        } else {
          patch = { [key]: num };
        }
      } else if (isTagsCol(key)) {
        const parts = String(draft || "")
          .split(/[;,]/g)
          .map(s => s.trim())
          .filter(Boolean);
        patch = { tagPath: parts }; // emit tagPath only
      } else if (/date/i.test(key)) {
        patch = { date: coerceDateISO(draft) || "" };
      } else if (isDescriptionCol(key)) {
        patch = { description: String(draft || "") };
      } else if (isAccountCol(key)) {
        patch = { accountName: String(draft || "") };
      } else {
        patch = { [key]: draft };
      }

      setSaving(true);
      await onCommitPatch?.(row, patch);
      setSaving(false);
      setEditingKey(null);
      setDraft("");
      return;
    } catch (err) {
      setSaving(false);
      const msg = String(err?.message || err || "Save failed");
      if (inputRef.current) inputRef.current.title = msg;
    }
  };

  const onKeyDownEditor = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  const SearchBtn = ({ label, onClick }) => (
    <button
      type="button"
      className={styles.cellAction}
      title={label}
      aria-label={label}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick?.(); }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    >
      <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.cellActionIcon} />
    </button>
  );

  const EditBtn = ({ label, onClick }) => (
    <button
      type="button"
      className={`${styles.cellAction} ${styles.cellActionEdit}`}
      title={label}
      aria-label={label}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick?.(); }}
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      disabled={saving}
    >
      <FontAwesomeIcon icon={faPen} className={styles.cellActionIcon} />
    </button>
  );

  return (
    <tr>
      {selectable && (
        <td className={styles.tableCell}>
          <input
            type="checkbox"
            className={styles.checkbox}
            aria-label={`Select row ${absoluteIndex + 1}`}
            checked={!!isChecked}
            onChange={(e) => onToggleRow?.(id, e.target.checked, row)}
          />
        </td>
      )}

      {visibleCols.map((key) => {
        // Special render for merged transfer "Amount" (adds a visual chip) and "Account"
        if (isMergedTransfer) {
          if (isAmountCol(key)) {
            const amt = Number(row?.amountAbs ?? row?.amount);
            const formatted = Number.isFinite(amt) ? formatAmountDisplay(Math.abs(amt)) : "";
            const editing = editingKey === key;
            return (
              <td key={key} className={["is-mono", styles.tableCell, "amount", styles.amount, styles.zero].join(" ")} title={formatted}>
                <div className={`truncate ${styles.cellInner}`}>
                  {editing ? (
                    <input
                      ref={inputRef}
                      type="text"
                      className={styles.cellInput}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={onKeyDownEditor}
                      disabled={saving}
                      placeholder="0.00"
                      inputMode="decimal"
                    />
                  ) : (
                    // <>
                    //   {/* Transfer chip + amount */}
                    //   <span className={`badge bg-secondary-subtle text-secondary border me-2 ${styles.transferChip}`}>
                    //     <FontAwesomeIcon icon={faArrowRightArrowLeft} className="me-1" />Transfer</span>
                    //   {formatted}
                    //   {inlineEditEnabled && isEditable(key) && !saving && (
                    //     <div className={styles.cellActions}>
                    //       <EditBtn label="Edit amount" onClick={() => beginEdit(key)} />
                    //     </div>
                    //   )}
                    // </>
                    <>
                      {/* Transfer chip + amount */}
                      <FontAwesomeIcon icon={faArrowRightArrowLeft} className={`me-1 ${rowstyles.transferArrows}`} />{formatted}
                      {inlineEditEnabled && isEditable(key) && !saving && (
                        <div className={styles.cellActions}>
                          <EditBtn label="Edit amount" onClick={() => beginEdit(key)} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </td>
            );
          }

          if (isAccountCol(key)) {
            const { left, right, both } = buildTransferRouteText(row, accounts);
            const editing = editingKey === key;

            return (
              <td key={key} className={["is-mono", "truncate", styles.tableCell].join(" ")} title={both}>
                <div className={`truncate ${styles.cellInner}`}>
                  {editing ? (
                    <div className="d-flex gap-2 align-items-center" style={{ minWidth: 220 }}>
                      <select
                        className={styles.cellInput}
                        value={fromIdDraft}
                        onChange={(e) => setFromIdDraft(e.target.value)}
                      >
                        <option value="">{`— From —`}</option>
                        {accounts.map(acc => (
                          <option key={String(acc.id)} value={String(acc.id)}>{acc.name}</option>
                        ))}
                      </select>
                      <span className="px-1">→</span>
                      <select
                        className={styles.cellInput}
                        value={toIdDraft}
                        onChange={(e) => setToIdDraft(e.target.value)}
                      >
                        <option value="">{`— To —`}</option>
                        {accounts.map(acc => (
                          <option key={String(acc.id)} value={String(acc.id)}>{acc.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className={styles.cellAction}
                        title="Save"
                        onClick={(e) => { e.preventDefault(); commitEdit(); }}
                      >
                        <FontAwesomeIcon icon={faPen} className={styles.cellActionIcon} />
                      </button>
                    </div>
                  ) : (
                    <>
                      {both}
                      {inlineEditEnabled && !saving && (
                        <div className={styles.cellActions}>
                          {/* Three targeted searches: source, route, destination */}
                          {left && left !== "?" && (
                            <SearchBtn
                              label={`Filter by account (From): ${left}`}
                              onClick={() => applyFilter("account", left)}
                            />
                          )}
                          <SearchBtn
                            label={`Filter by route: ${both}`}
                            onClick={() => applyFilter("account", both)}
                          />
                          {right && right !== "?" && (
                            <SearchBtn
                              label={`Filter by account (To): ${right}`}
                              onClick={() => applyFilter("account", right)}
                            />
                          )}
                          {isEditable(key) && (
                            <EditBtn label="Edit From/To" onClick={() => beginEdit(key)} />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </td>
            );
          }
        }

        const raw = row?.[key];

        // ----- TAGS (chips, with inline edit icon only) -----
        if (isTagsCol(key)) {
          const paths = normalizeTagsForDisplay(row);
          const editing = editingKey === key;

          return (
            <td key={key} className={`${styles.tableCell} ${styles.tagsCell}`}>
              <div className={`truncate ${styles.cellInner} ${styles.searchable}`}>
                {editing ? (
                  <input
                    ref={inputRef}
                    type="text"
                    className={styles.cellInput}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={onKeyDownEditor}
                    placeholder="tag1/sub, tag2"
                    disabled={saving}
                  />
                ) : (
                  <>
                    {tagsAsChips ? (
                      <div className={styles.tagWrap}>
                        {paths.length === 0 ? (
                          <span className="text-secondary small">—</span>
                        ) : (
                          paths.map((p, idx2) => (
                            <span
                              key={`${p}-${idx2}`}
                              className={`${styles.tag} ${styles.tagClickable}`}
                              title={`Filter by tag: ${p}`}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); applyFilter("tag", p); }}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); applyFilter("tag", p); } }}
                            >
                              <span className={styles.tagText}>{p}</span>
                            </span>
                          ))
                        )}
                      </div>
                    ) : (
                      <span className="text-muted small" title={paths.join(", ")}>
                        {paths.length ? paths.join(", ") : "—"}
                      </span>
                    )}
                    {inlineEditEnabled && isEditable(key) && !saving && (
                      <div className={styles.cellActions}>
                        <EditBtn label="Edit tags" onClick={() => beginEdit(key)} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </td>
          );
        }

        // ----- AMOUNT / BALANCE for non-merged rows -----
        if (isAmountCol(key) || isBalanceCol(key)) {
          const num = parseAmountValue(raw);
          const signClass = Number.isNaN(num)
            ? "zero"
            : num > 0
              ? "positive"
              : num < 0
                ? "negative"
                : "zero";

          const editing = editingKey === key;

          if (isBalanceCol(key)) {
            const formatted = Number.isNaN(num) ? String(raw ?? "") : formatAmountDisplay(num);
            return (
              <td key={key} className={["is-mono", styles.tableCell, "amount", signClass, styles.amount, styles[signClass]].join(" ")} title={formatted}>
                {formatted}
              </td>
            );
          }

          const formatted = Number.isFinite(num) ? formatAmountDisplay(num) : String(raw ?? "");
          return (
            <td key={key} className={["is-mono", styles.tableCell, "amount", signClass, styles.amount, styles[signClass]].join(" ")} title={formatted}>
              <div className={`truncate ${styles.cellInner}`}>
                {editing ? (
                  <input
                    ref={inputRef}
                    type="text"
                    className={styles.cellInput}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={onKeyDownEditor}
                    disabled={saving}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                ) : (
                  <>
                    {formatted}
                    {inlineEditEnabled && isEditable(key) && !saving && (
                      <div className={styles.cellActions}>
                        <EditBtn label="Edit amount" onClick={() => beginEdit(key)} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </td>
          );
        }

        // ----- GENERIC (date / description / account / other) -----
        const isDescription = isDescriptionCol(key);
        const isDate = /date/i.test(key);
        const isAccount = isAccountCol(key);

        const base = [
          "is-mono",
          (!isDescription || !wrapDescription) ? "truncate" : "",
          styles.tableCell,
          isDate ? styles.dateCell : "",
          isDescription ? styles.descriptionCell : "",
        ];

        // For Account column on single-leg transfer, render the fallback route and
        // make the search target the actual leg account (not the "A → ?" string).
        const routeText = (isAccount && isTransfer && !isMergedTransfer) ? buildSingleLegRouteText(row) : null;

        const text = routeText
          ? routeText.both
          : (() => {
            const val = row?.[key];
            if (isAccount) {
              const str = (val == null || val === "") ? accountNameFromId(row?.accountId, accounts) : val;
              return str ? String(str) : "";
            }
            if (val === null) return "null";
            if (typeof val === "object") return safeJSONStringify(val);
            if (typeof val === "undefined") return "";
            return String(val);
          })();

        const editing = editingKey === key;

        return (
          <td key={key} className={base.filter(Boolean).join(" ")} title={text}>
            <div className={`truncate ${styles.cellInner} ${isSearchable(key) ? styles.searchable : ""}`}>
              {editing ? (
                isAccount ? (
                  <select
                    ref={inputRef}
                    className={styles.cellInput}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={onKeyDownEditor}
                    disabled={saving}
                  >
                    <option value="">— Pick account —</option>
                    {accountOptions.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    ref={inputRef}
                    type={isDate ? "date" : "text"}
                    className={styles.cellInput}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={onKeyDownEditor}
                    disabled={saving}
                    placeholder={isDate ? "YYYY-MM-DD" : ""}
                  />
                )
              ) : (
                <>
                  {text}
                  {inlineEditEnabled && !saving && (
                    <div className={styles.cellActions}>
                      {isSearchable(key) && (
                        routeText ? (
                          // Search by the leg's actual account (not the "A → ?" string)
                          <SearchBtn
                            label={`Filter by account: ${routeText.left !== "?" ? routeText.left : routeText.right}`}
                            onClick={() => {
                              const target = routeText.left !== "?" ? routeText.left : routeText.right;
                              applyFilter("account", target);
                            }}
                          />
                        ) : (
                          <SearchBtn
                            label={
                              isDate
                                ? `Filter by ${coerceDateISO(text)}`
                                : isAccount
                                  ? `Filter by account: ${text}`
                                  : `Search: “${text}”`
                            }
                            onClick={() => {
                              if (isDate) applyFilter("date", text);
                              else if (isAccount) applyFilter("account", text);
                              else applyFilter("q", text);
                            }}
                          />
                        )
                      )}
                      {isEditable(key) && (
                        <EditBtn
                          label={`Edit ${isAccount ? "account" : key}`}
                          onClick={() => beginEdit(key)}
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </td>
        );
      })}
    </tr>
  );
}
